import {
    AcceptAudienceErrorEvent,
    AcceptServiceRequestErrorEvent,
    AudienceAcceptedEvent,
    AudienceClosedEvent,
    AudienceEndedEvent,
    EmbedEvent,
    EmbedEvents,
} from 'embed/types/events'
import {
    NinchatChannelJoinedEvent,
    NinchatChannelPartedEvent,
    NinchatChannelUpdatedEvent,
} from 'api/types/events'
import {NinchatEnvironmentConfig} from 'embed/types'
import {acceptAudience} from 'api/action/acceptAudience'
import {assignOldestTranscript} from 'api/action/assignOldestTranscript'
import {channelParted} from 'api/event/channelParted'
import {channelUpdated} from 'api/event/channelUpdated'
import {composeMetadata} from 'embed/utils/metadata'
import {config} from 'embed/listeners'
import {ninchatEventSlice} from 'api/slice'
import {partChannel} from 'api/action/partChannel'
import {push} from 'navigation/actions'
import {startAppListening} from 'middlewares/listener'
import {updateChannel} from 'api/action/updateChannel'

type Effect = Parameters<typeof startAppListening>[0]['effect']
type Predicate = Parameters<typeof startAppListening>[0]['predicate']

const audienceAcceptedListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent) => ({
    effect: (action) => {
        const targetOrigin = config.get('targetOrigin')
        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const payload = action.payload as NinchatChannelJoinedEvent

        const {
            channel_attrs: {audience_id},
            channel_members_metadata,
        } = payload

        if (!audience_id) {
            console.error('missing audience_id')
            return
        }


        const metadata = composeMetadata(channel_members_metadata, targetOrigin)

        const audienceAcceptedEvent: AudienceAcceptedEvent = {
            ...metadata.length && {metadata},
            audience_id,
            event: EmbedEvent.AudienceAccepted,
            targetOrigin,
        }

        sendToParent(audienceAcceptedEvent)
    },
    predicate: (action) => action.type === acceptAudience.fulfilled.type,
})

const audienceEndedListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent) => ({
    effect: (action) => {
        const targetOrigin = config.get('targetOrigin')
        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const payload = action.payload as NinchatChannelUpdatedEvent

        const {audience_id} = payload.channel_attrs

        if (!audience_id) {
            console.error('missing audience_id')
            return
        }

        const audienceEndedEvent: AudienceEndedEvent = {
            audience_id,
            event: EmbedEvent.AudienceEnded,
            targetOrigin,
        }

        sendToParent(audienceEndedEvent)
    },
    predicate: (action) => {
        const triggerEvents = [
            updateChannel.fulfilled.type,
            channelUpdated.fulfilled.type,
        ]

        if (!triggerEvents.includes(action.type)) {
            return false
        }

        const payload = action.payload as NinchatChannelUpdatedEvent

        return !!payload.channel_attrs.audience_id && !!payload.channel_attrs.closed
    },
})

const audienceClosedListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent: any) => ({
    effect: (action, listenerApi) => {
        const targetOrigin = config.get('targetOrigin')

        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const payload = action.payload as NinchatChannelPartedEvent

        const {channel_id} = payload

        const audience_id = listenerApi.getOriginalState().ninchat.user_channels[channel_id]?.channel_attrs.audience_id

        if (!audience_id) {
            console.error('missing audience_id')
            return
        }

        const audienceClosedEvent: AudienceClosedEvent = {
            audience_id,
            event: EmbedEvent.AudienceClosed,
            targetOrigin,
        }

        sendToParent(audienceClosedEvent)
    },
    predicate: (action, _currentState, previousState) => {
        const triggerEvents = [
            partChannel.fulfilled.type,
            channelParted.fulfilled.type,
        ]

        if (!triggerEvents.includes(action.type)) {
            return false
        }

        const payload = action.payload as NinchatChannelPartedEvent

        const {channel_id} = payload

        const audience_id = previousState.ninchat.user_channels[channel_id]?.channel_attrs.audience_id

        return !!audience_id
    },
})

export const audienceEnqueuedListener = {
    effect: (action: any, listenerApi: any) => {
        const {queue_id} = action.payload

        listenerApi.dispatch(push({
            name: 'EmbedQueue',
            params: {queue_id},
        }))
    },
    type: `${ninchatEventSlice.actions.audience_enqueued}`,
}

const audienceAcceptRejectedListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent) => ({
    effect: (action) => {
        const targetOrigin = config.get('targetOrigin')
        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const {
            queue_id, audience_id,
        } = action.meta.arg
        const reason = typeof action.payload === 'string' ? action.payload : undefined

        const acceptAudienceErrorEvent: AcceptAudienceErrorEvent = {
            event: EmbedEvent.AcceptAudienceError,
            queue_id,
            targetOrigin,
            ...audience_id && {audience_id},
            ...reason && {reason},
        }

        sendToParent(acceptAudienceErrorEvent)
    },
    predicate: (action) => action.type === acceptAudience.rejected.type,
})

const serviceRequestRejectedListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent) => ({
    effect: (action) => {
        const targetOrigin = config.get('targetOrigin')
        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const {queue_id} = action.meta.arg
        const reason = typeof action.payload === 'string' ? action.payload : undefined

        const acceptServiceRequestErrorEvent: AcceptServiceRequestErrorEvent = {
            event: EmbedEvent.AcceptServiceRequestError,
            queue_id,
            targetOrigin,
            ...reason && {reason},
        }

        sendToParent(acceptServiceRequestErrorEvent)
    },
    predicate: (action) => action.type === assignOldestTranscript.rejected.type,
})

export const start = (sendToParent: (event: EmbedEvents) => void, conf: NinchatEnvironmentConfig) => {
    const listeners = [
        startAppListening(audienceAcceptedListener(sendToParent)),
        startAppListening(audienceEndedListener(sendToParent)),
        startAppListening(audienceClosedListener(sendToParent)),
        startAppListening(audienceAcceptRejectedListener(sendToParent)),
        startAppListening(serviceRequestRejectedListener(sendToParent)),
    ]
    if (conf?.variant === 'queue' || conf?.variant === 'channel') {
        startAppListening(audienceEnqueuedListener)
    }
    return listeners
}
